// Boveda de accesos (correo-api, boveda.mjs): la lista nunca trae claves; «Ver» las descifra y queda registro.
//   GET    /correo/api/boveda?q=&papelera=1   -> { accesos: [...], misGrupos: [ids], admin }  (solo lo que puedes ver)
//   GET    /correo/api/boveda/<id>            -> ficha sin secretos + vistas (ultimas 10 «ver»/«copiar»)
//   POST   /correo/api/boveda/<id>/ver        -> { usuario, clave, notas }   (registra quien la vio; 30 por minuto)
//   POST   /correo/api/boveda/<id>/copiar     registra la copia (sin cuerpo)
//   POST   /correo/api/boveda                 crea;  PATCH /<id> edita;  DELETE /<id> papelera;  POST /<id>/restaurar
// Los grupos van por id (buzonGrupo). Si el servicio responde 404 o 503 la boveda aun no esta activa.
import { ApiError, request } from './http';

export type Acceso = {
  id: string;
  titulo: string;
  url: string;
  categoria: string;
  grupos: string[];
  articuloId: string | null;
  tieneUsuario: boolean;
  tieneClave: boolean;
  tieneNotas: boolean;
  creadoPor: string | null;
  creadoEn: string | null;
  editadoEn: string | null;
  borradoEn: string | null;
  expiraBorrado: string | null;
};
export type ListaAccesos = { accesos: Acceso[]; misGrupos: string[]; admin: boolean; papelera: boolean };
export type Secreto = { usuario: string; clave: string; notas: string };
export type Vista = { miembro: string | null; accion: string; en: string };
export type DatosAcceso = { titulo: string; url: string; usuario: string; clave: string; notas: string; grupos: string[] };

type RawAcceso = Partial<Omit<Acceso, 'url' | 'categoria'>> & { id: string; url?: string | null; categoria?: string | null };

const deRaw = (a: RawAcceso): Acceso => ({
  id: a.id,
  titulo: a.titulo ?? 'Acceso',
  url: a.url ?? '',
  categoria: a.categoria ?? '',
  grupos: Array.isArray(a.grupos) ? a.grupos : [],
  articuloId: a.articuloId ?? null,
  tieneUsuario: !!a.tieneUsuario,
  tieneClave: !!a.tieneClave,
  tieneNotas: !!a.tieneNotas,
  creadoPor: a.creadoPor ?? null,
  creadoEn: a.creadoEn ?? null,
  editadoEn: a.editadoEn ?? null,
  borradoEn: a.borradoEn ?? null,
  expiraBorrado: a.expiraBorrado ?? null,
});

export class BovedaInactiva extends Error {}

const inactiva = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 503);

export async function listarAccesos(opts: { q?: string; papelera?: boolean } = {}): Promise<ListaAccesos> {
  try {
    const r = await request<{ accesos?: RawAcceso[]; misGrupos?: string[]; admin?: boolean; papelera?: boolean }>('/correo/api/boveda', { query: { q: opts.q, papelera: opts.papelera ? 1 : undefined } });
    return { accesos: (r.accesos ?? []).map(deRaw), misGrupos: r.misGrupos ?? [], admin: !!r.admin, papelera: !!(r.papelera ?? r.admin) };
  } catch (e) {
    if (inactiva(e)) throw new BovedaInactiva('La bóveda aún no está activa');
    throw e;
  }
}

// Mensaje claro cuando se pasa el limite de claves vistas por minuto.
const conAviso429 = async <T>(p: Promise<T>): Promise<T> => {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 429) throw new ApiError(429, 'Viste muchas claves en un minuto. Espera un momento y vuelve a intentarlo.');
    throw e;
  }
};

export const verSecreto = async (id: string): Promise<Secreto> => {
  const r = await conAviso429(request<Partial<Secreto> | null>(`/correo/api/boveda/${id}/ver`, { method: 'POST', sinReintento: true }));
  return { usuario: r?.usuario ?? '', clave: r?.clave ?? '', notas: r?.notas ?? '' };
};
export const registrarCopia = (id: string) => request(`/correo/api/boveda/${id}/copiar`, { method: 'POST', sinReintento: true }).catch(() => undefined);
export const vistasDe = async (id: string): Promise<Vista[]> => (await request<{ vistas?: Vista[] }>(`/correo/api/boveda/${id}`)).vistas ?? [];
export const crearAcceso = async (d: DatosAcceso) => deRaw(await request<RawAcceso>('/correo/api/boveda', { method: 'POST', body: d }));
export const editarAcceso = async (id: string, d: Partial<DatosAcceso>) => deRaw(await request<RawAcceso>(`/correo/api/boveda/${id}`, { method: 'PATCH', body: d }));
export const borrarAcceso = (id: string) => request(`/correo/api/boveda/${id}`, { method: 'DELETE' });
export const restaurarAcceso = (id: string) => request(`/correo/api/boveda/${id}/restaurar`, { method: 'POST' });

// Datos para PATCH: usuario, clave y notas vacios no se mandan (el servidor los deja como estaban).
export function cambiosDeEdicion(v: DatosAcceso): Partial<DatosAcceso> {
  const d: Partial<DatosAcceso> = { titulo: v.titulo, url: v.url, grupos: v.grupos };
  if (v.usuario.trim()) d.usuario = v.usuario;
  if (v.clave) d.clave = v.clave;
  if (v.notas.trim()) d.notas = v.notas;
  return d;
}

// Clave al azar de `largo` caracteres con letras, numeros y simbolos (sin los que se confunden: 0 O l 1 I).
export function generarClave(largo = 20) {
  const letras = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const simbolos = '!#$%&*+-=?@_';
  const todo = letras + simbolos;
  const azar = new Uint32Array(largo);
  crypto.getRandomValues(azar);
  const c = [...azar].map((n) => todo[n % todo.length]);
  // Al menos un simbolo y un numero.
  c[azar[0] % largo] = simbolos[azar[1] % simbolos.length];
  c[(azar[0] + 1) % largo] = '23456789'[azar[2] % 8];
  return c.join('');
}
